'use client';

// module imports
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// file imports
import { LoadingState, NewQuestionForm, QuestionEditor, QuestionHeader, QuestionNavigation, QuestionSidebar, SaveConfirmationModal } from '@/components';
import { questionTypeAndCategory, replacePlaceholders } from '@/constants';
import { addFeedbackQuestion, getFeedbackQuestions, updateFeedbackQuestion, updateQuestionOrder } from '@/redux/slices/feedbackQuestionSlice';
import type { AppDispatch, RootState } from '@/redux/store';
import { IQuestion, QuestionOption, isQuestionOptionObject, isStringOption } from '@/types';

interface QuestionSelectorProps {
  slug: string;
  onQuestionsChange?: (questions: IQuestion[]) => void;
  showSaveButton?: boolean;
  className?: string;
  speakerName?: string;
}

export default function QuestionSelector({ slug, onQuestionsChange, showSaveButton = true, className = '', speakerName }: QuestionSelectorProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading, error } = useSelector((state: RootState) => state.feedbackQuestion);
  const { data: user } = useSelector((state: RootState) => state.auth);
  const questions = useMemo(() => data?.questions || [], [data?.questions]);
  const [selectedQuestion, setSelectedQuestion] = useState<IQuestion | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showNewQuestionInput, setShowNewQuestionInput] = useState(false);
  const [selectedQuestionType, setSelectedQuestionType] = useState<string>('');
  const [newQuestionLabel, setNewQuestionLabel] = useState('');
  const [newQuestionDescription, setNewQuestionDescription] = useState('');
  const [editingQuestion, setEditingQuestion] = useState<IQuestion | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, boolean>>({});
  const [requiredOptions, setRequiredOptions] = useState<Record<string, boolean>>({});
  const [orderedQuestions, setOrderedQuestions] = useState<IQuestion[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalQuestion, setOriginalQuestion] = useState<IQuestion | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<IQuestion | null>(null);
  const [activeTab, setActiveTab] = useState<'questions' | 'preview'>('questions');

  const finalSpeakerName = speakerName || 'Speaker';
  const canAddCustomQuestions = user?.subscription?.canAddCustomQuestions || false;
  const canEditQuestions = user?.subscription?.canEditQuestions || false;

  useEffect(() => {
    if (slug) {
      dispatch(getFeedbackQuestions(slug));
    }
  }, [dispatch, slug]);

  // Initialize ordered questions when questions are loaded
  useEffect(() => {
    if (questions && questions.length > 0) {
      setOrderedQuestions(questions);
    }
  }, [questions]);

  // Initialize with the first question selected by default
  useEffect(() => {
    if (questions && questions.length > 0 && !selectedQuestion) {
      const firstQuestion = questions[0];
      const questionWithSpeakerName = {
        ...firstQuestion,
        description: firstQuestion.description ? replacePlaceholders(firstQuestion.description, finalSpeakerName) : undefined,
      };
      setSelectedQuestion(questionWithSpeakerName);
      setEditingQuestion(questionWithSpeakerName);
      setOriginalQuestion(questionWithSpeakerName); // Set original question for change detection
      setHasUnsavedChanges(false); // Reset unsaved changes flag
    }
  }, [questions, finalSpeakerName, data, selectedQuestion]);

  // Notify parent of questions change
  useEffect(() => {
    if (onQuestionsChange && selectedQuestion) {
      onQuestionsChange([selectedQuestion]);
    }
  }, [selectedQuestion, onQuestionsChange]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (showSaveModal) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [showSaveModal]);

  const handleQuestionSelect = (question: IQuestion) => {
    setSelectedQuestion(question);
    setEditingQuestion(question);
    setOriginalQuestion(question); // Store original state
    setHasUnsavedChanges(false); // Reset unsaved changes flag

    // Initialize selected options for the new question
    const initialOptions: Record<string, boolean> = {};
    const initialRequired: Record<string, boolean> = {};
    if (question.options) {
      question.options.forEach((option) => {
        const optionKey = typeof option === 'string' ? option : option.label;

        if (question.type === 'contact') {
          // For contact info questions, check if this option was previously selected
          // For regular contact fields (email, name), they should be selected by default
          // For under-18 option, check the required field to determine if it was selected
          if (isQuestionOptionObject(option) && option.isUnder18Option) {
            // For under-18 option, check the required field (this indicates if it was selected)
            const wasSelected = option.required || false;
            initialOptions[optionKey] = wasSelected;
          } else {
            // Regular contact fields (email, name) are always selected
            initialOptions[optionKey] = true;
          }
        } else {
          // For other questions, check if this option was previously selected
          const wasSelected = question.options?.includes(optionKey) || false;
          initialOptions[optionKey] = wasSelected;
        }

        // Check if this option was previously required (from the option object itself)
        const wasRequired = isQuestionOptionObject(option) ? option.required || false : false;
        initialRequired[optionKey] = wasRequired;
      });
    }
    setSelectedOptions(initialOptions);
    setRequiredOptions(initialRequired);
  };

  const handleQuestionDescriptionChange = (newDescription: string) => {
    if (editingQuestion) {
      const updatedQuestion = {
        ...editingQuestion,
        description: newDescription,
      };
      setEditingQuestion(updatedQuestion);
      setSelectedQuestion(updatedQuestion);

      // Check if there are unsaved changes
      const originalDescription = originalQuestion?.description || '';
      const hasChanges = originalDescription !== newDescription;
      setHasUnsavedChanges(hasChanges);
    }
  };

  const handleOptionToggle = (optionKey: string) => {
    setSelectedOptions((prev) => {
      const newOptions = {
        ...prev,
        [optionKey]: !prev[optionKey],
      };

      checkForChanges(newOptions, requiredOptions);

      return newOptions;
    });
  };

  const handleRequiredToggle = (optionKey: string) => {
    setRequiredOptions((prev) => {
      const newRequired = {
        ...prev,
        [optionKey]: !prev[optionKey],
      };

      checkForChanges(selectedOptions, newRequired);

      return newRequired;
    });
  };

  const checkForChanges = (currentSelected: Record<string, boolean>, currentRequired: Record<string, boolean>) => {
    // For contact info questions, check under-18 option changes and required status changes
    // For other questions, check both option and required changes
    const originalOptions = originalQuestion?.options || [];

    let hasOptionChanges = false;
    if (originalQuestion?.type === 'contact') {
      // For contact questions, check under-18 option changes
      // Find the under-18 option in original question
      const originalUnder18Option = originalOptions.find((opt: QuestionOption) => isQuestionOptionObject(opt) && opt.isUnder18Option);
      const originalUnder18Key = originalUnder18Option ? (isStringOption(originalUnder18Option) ? originalUnder18Option : originalUnder18Option.label) : '';

      // Check if under-18 option selection has changed
      if (originalUnder18Key && originalUnder18Option && isQuestionOptionObject(originalUnder18Option)) {
        const wasUnder18Selected = originalUnder18Option.required || false;
        const isUnder18Selected = currentSelected[originalUnder18Key] || false;
        hasOptionChanges = wasUnder18Selected !== isUnder18Selected;
      }
    } else {
      // For other questions, check all option changes
      hasOptionChanges = originalOptions.some((option: QuestionOption) => {
        const optionLabel = isStringOption(option) ? option : option.label;
        const wasSelected = originalOptions.includes(optionLabel);
        const isSelected = currentSelected[optionLabel] || false;
        return wasSelected !== isSelected;
      });
    }

    // Check if there are unsaved changes to required status
    const hasRequiredChanges = originalOptions.some((option: QuestionOption) => {
      const optionLabel = isStringOption(option) ? option : option.label;
      const wasRequired = isQuestionOptionObject(option) ? option.required || false : false;
      const isRequired = currentRequired[optionLabel] || false;
      return wasRequired !== isRequired;
    });

    // Also check description changes
    const originalDescription = originalQuestion?.description || '';
    const currentDescription = editingQuestion?.description || '';
    const hasDescriptionChanges = originalDescription !== currentDescription;

    setHasUnsavedChanges(hasOptionChanges || hasRequiredChanges || hasDescriptionChanges);
  };

  const handleQuestionReorder = (newOrder: IQuestion[]) => {
    // Store the current order for potential rollback
    const previousOrder = [...orderedQuestions];

    // Update the state
    setOrderedQuestions(newOrder);

    // Optimistically save the order in the background
    if (canEditQuestions && newOrder.length > 0) {
      handleSaveQuestionOrderOptimistic(previousOrder, newOrder);
    }
  };

  const handleSaveQuestions = async () => {
    if (!slug || !selectedQuestion || !editingQuestion) return;

    setIsSaving(true);
    try {
      // Check if under-18 option is selected
      const under18Option = selectedQuestion.options?.find((opt: QuestionOption) => isQuestionOptionObject(opt) && opt.isUnder18Option);
      const under18OptionKey = under18Option ? (isStringOption(under18Option) ? under18Option : under18Option.label) : '';
      const isUnder18Selected = selectedOptions[under18OptionKey] || false;

      // Update the question options with new required status and under-18 option
      const updatedOptions = selectedQuestion.options?.map((option: QuestionOption): QuestionOption => {
        const optionKey = isStringOption(option) ? option : option.label;
        const isRequired = requiredOptions[optionKey] || false;
        const isEmailOrName = optionKey === 'Ask for Attendee Email' || optionKey === 'Ask for Attendee Name';

        if (isQuestionOptionObject(option)) {
          if (option.isUnder18Option) {
            // For under-18 option, update the required field based on selection
            return {
              ...option,
              required: isUnder18Selected,
            };
          } else if (isEmailOrName && isUnder18Selected) {
            // For email/name options, set required to false when under-18 is selected
            return {
              ...option,
              required: false,
            };
          } else {
            // For regular options, update the required field normally
            return {
              ...option,
              required: isRequired,
            };
          }
        }
        return option;
      });

      // Check if description has changed
      const originalDescription = originalQuestion?.description || '';
      const currentDescription = editingQuestion?.description || '';
      const hasDescriptionChanged = originalDescription !== currentDescription;

      const questionData = {
        ...(hasDescriptionChanged && { description: currentDescription }),
        options: updatedOptions,
        ...(hasDescriptionChanged && { hasBeenCustomized: true }),
      };

      await dispatch(updateFeedbackQuestion({ slug, questionId: selectedQuestion._id, questionData })).unwrap();
      dispatch(getFeedbackQuestions(slug));

      // Reset unsaved changes flag after successful save
      setHasUnsavedChanges(false);
      setOriginalQuestion(editingQuestion);
    } catch (error) {
      console.error('Error saving question:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveQuestionOrderOptimistic = async (previousOrder: IQuestion[], newQuestions: IQuestion[]) => {
    if (!slug) {
      return;
    }

    const orderedQuestionIds = newQuestions.map((q) => q._id);

    try {
      await dispatch(updateQuestionOrder({ slug, orderedQuestionIds })).unwrap();
      // No need to re-fetch questions since we're using optimistic updates
    } catch (error) {
      console.error('Error saving question order:', error);
      // Rollback to previous order on error
      setOrderedQuestions(previousOrder);
    }
  };

  const handleNavigationWithSave = (targetQuestion: IQuestion) => {
    if (hasUnsavedChanges && canEditQuestions) {
      setPendingNavigation(targetQuestion);
      setShowSaveModal(true);
    } else {
      handleQuestionSelect(targetQuestion);
    }
  };

  const handleSaveAndNavigate = async () => {
    await handleSaveQuestions();
    if (pendingNavigation) {
      handleQuestionSelect(pendingNavigation);
    }
    setShowSaveModal(false);
    setPendingNavigation(null);
  };

  const handleDiscardAndNavigate = () => {
    if (pendingNavigation) {
      handleQuestionSelect(pendingNavigation);
    }
    setShowSaveModal(false);
    setPendingNavigation(null);
  };

  const handleToggleQuestion = async (questionId: string) => {
    const question = questions.find((q: IQuestion) => q._id === questionId);
    if (!question) {
      console.log('Question not found:', questionId);
      return;
    }

    // Optimistic update - immediately update the local state
    const updatedQuestions = questions.map((q: IQuestion) => (q._id === questionId ? { ...q, isEnabled: !q.isEnabled, hasBeenCustomized: true } : q));
    setOrderedQuestions(updatedQuestions);

    // Update selectedQuestion if it's the one being toggled
    if (selectedQuestion && selectedQuestion._id === questionId) {
      const updatedSelectedQuestion = {
        ...selectedQuestion,
        isEnabled: !question.isEnabled,
        hasBeenCustomized: true,
      };
      setSelectedQuestion(updatedSelectedQuestion);
      setEditingQuestion(updatedSelectedQuestion);
    }

    setIsSaving(true);
    try {
      const questionData = {
        isEnabled: !question.isEnabled,
        hasBeenCustomized: true,
      };

      await dispatch(updateFeedbackQuestion({ slug, questionId, questionData })).unwrap();
      dispatch(getFeedbackQuestions(slug));
    } catch (error) {
      console.error('Error toggling question:', error);
      // Revert optimistic update on error
      setOrderedQuestions(questions);
      // Revert selectedQuestion if it was the one being toggled
      if (selectedQuestion && selectedQuestion._id === questionId) {
        setSelectedQuestion(question);
        setEditingQuestion(question);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddCustomQuestion = async () => {
    if (!slug || !selectedQuestionType || !newQuestionLabel.trim()) return;

    // Check if user has reached the 20 question limit
    if (questions.length >= 20) {
      alert('You have reached the maximum limit of 20 questions. Please delete some questions before adding new ones.');
      return;
    }

    const questionConfig = questionTypeAndCategory.find((q) => q.type === selectedQuestionType);
    const newQuestion = {
      label: newQuestionLabel.trim(),
      type: selectedQuestionType,
      category: questionConfig?.category || selectedQuestionType,
      description: newQuestionDescription.trim() || undefined,
      reportKey: questionConfig?.reportKey || `${selectedQuestionType}_question`,
      options: questionConfig?.options || null,
    };

    try {
      await dispatch(addFeedbackQuestion({ slug, question: newQuestion })).unwrap();
      dispatch(getFeedbackQuestions(slug));
      setShowNewQuestionInput(false);
      setSelectedQuestionType('');
      setNewQuestionLabel('');
      setNewQuestionDescription('');
    } catch (error) {
      console.error('Error adding custom question:', error);
    }
  };

  const handleCancelNewQuestion = () => {
    setShowNewQuestionInput(false);
    setSelectedQuestionType('');
    setNewQuestionLabel('');
    setNewQuestionDescription('');
  };

  const getQuestionTypeLabel = (type: string) => {
    const questionConfig = questionTypeAndCategory.find((q) => q.type === type);
    return questionConfig?.label || 'Unknown';
  };

  if (loading) {
    return <LoadingState message="Loading feedback questions..." />;
  }

  if (error) {
    return <LoadingState message={`Error loading feedback questions: ${error}`} />;
  }

  return (
    <div className={`flex flex-col lg:flex-row gap-2 sm:gap-3 lg:gap-6 ${className}`}>
      {/* Main Content Area */}
      <div className="flex-1">
        {/* Header */}
        <QuestionHeader
          selectedQuestion={selectedQuestion}
          questions={questions}
          canAddCustomQuestions={canAddCustomQuestions}
          onAddQuestion={() => {
            setShowNewQuestionInput(true);
            setSelectedQuestionType('');
            setNewQuestionLabel('');
            setNewQuestionDescription('');
          }}
          getQuestionTypeLabel={getQuestionTypeLabel}
          showSaveButton={showSaveButton}
        />

        {/* New Question Input Form */}
        <NewQuestionForm showNewQuestionInput={showNewQuestionInput} selectedQuestionType={selectedQuestionType} newQuestionLabel={newQuestionLabel} newQuestionDescription={newQuestionDescription} questions={questions} onQuestionTypeChange={setSelectedQuestionType} onQuestionLabelChange={setNewQuestionLabel} onQuestionDescriptionChange={setNewQuestionDescription} onAddQuestion={handleAddCustomQuestion} onCancel={handleCancelNewQuestion} getQuestionTypeLabel={getQuestionTypeLabel} />

        {/* Selected Question */}
        <QuestionEditor selectedQuestion={selectedQuestion} editingQuestion={editingQuestion} orderedQuestions={orderedQuestions} selectedOptions={selectedOptions} requiredOptions={requiredOptions} canEditQuestions={canEditQuestions} onDescriptionChange={handleQuestionDescriptionChange} onOptionToggle={handleOptionToggle} onRequiredToggle={handleRequiredToggle} />

        {/* Navigation Buttons */}
        <QuestionNavigation
          selectedQuestion={selectedQuestion}
          orderedQuestions={orderedQuestions}
          onNavigate={(question) => {
            const questionWithSpeakerName = {
              ...question,
              description: question.description ? replacePlaceholders(question.description, finalSpeakerName) : undefined,
            };
            handleNavigationWithSave(questionWithSpeakerName);
          }}
          showSaveButton={showSaveButton}
        />
      </div>

      {/* Right Sidebar - All Questions */}
      <div className="w-full lg:w-80 flex justify-center lg:justify-start">
        <div className="w-full max-w-xs sm:max-w-sm lg:max-w-none">
          <QuestionSidebar questions={orderedQuestions} selectedQuestion={selectedQuestion} onQuestionSelect={handleNavigationWithSave} onToggleQuestion={handleToggleQuestion} speakerName={finalSpeakerName} activeTab={activeTab} onTabChange={setActiveTab} onReorder={handleQuestionReorder} canEditQuestions={canEditQuestions} />
        </div>
      </div>

      {/* Save Confirmation Modal */}
      <SaveConfirmationModal isOpen={showSaveModal} isSaving={isSaving} onSaveAndContinue={handleSaveAndNavigate} onDiscardAndContinue={handleDiscardAndNavigate} />
    </div>
  );
}
